import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import env from './utils/env';

import { useAuth } from './contexts/AuthContext';
import './ReportSourcesView.css';
import DocumentDetailView, { DocumentDetails, SearchResult } from './DocumentDetailView';

// Define proper types
interface ReportSource {
    _id: string;
    name: string;
    description?: string;
    url?: string;
    count?: number;
    filename?: string;
    is_pdf?: boolean;
    token_size?: number;
    update_timestamp?: string;
    extracted_timestamp?: string;
    metadata?: {
        title?: string;
        author?: string;
        date?: string;
        document_type?: string;
        language?: string;
        subject?: string;
        keywords?: string[];
    };
    metadata_validation_details?: {
        title?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        author?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        date?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        keywords?: {
            field: string;
            value: string[];
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        subject?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
    };
    metadata_validated?: string;
}

interface ApiResponse {
    searchResults?: SearchResult[];
    reportSources: ReportSource[];
}

const EmbeddingsSearchView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const [reportSources, setReportSources] = useState<ReportSource[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
    const [documentText, setDocumentText] = useState<string | null>(null);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [isPdf, setIsPdf] = useState<boolean>(false);
    const [documentTitle, setDocumentTitle] = useState<string>('');
    const [loadingDocument, setLoadingDocument] = useState<boolean>(false);
    const [documentDetails, setDocumentDetails] = useState<DocumentDetails | null>(null);
    const [documentChunks, setDocumentChunks] = useState<SearchResult[]>([]);
    // New state to track which document's chunks are being shown in the table
    const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(null);
    const [patternEnabled, setPatternEnabled] = useState<boolean>(false);
    const [patternError, setPatternError] = useState<string | null>(null);

    // Handle search input change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    // Toggle pattern filtering on/off
    const togglePatternFilter = () => {
        setPatternEnabled(!patternEnabled);
        setPatternError(null);
    };

    // Get the current regex pattern to use
    const getCurrentPattern = (): string | undefined => {
        if (!patternEnabled || !searchQuery.trim()) return undefined;
        
        // Use the search query as the exact match pattern
        return escapeRegExp(searchQuery);
    };

    // Perform search on submit
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!authLoaded) return;
        
        if (!searchQuery.trim()) {
            // If search query is empty, show all report sources
            setReportSources(reportSources);
            return;
        }
        setIsSearching(true);
        // Get the regex pattern to use (if any)
        const regexPattern = getCurrentPattern();
        
        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`,
            'Content-Type': 'application/json'
        };
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Make the API call with the pattern
        axios.post<ApiResponse>(`${apiUrl}/report-sources/search`, {
            query: searchQuery,
            topK: 20,
            regexPattern
        }, {
            headers
        })
            .then(response => {
                if (response.data.searchResults && Array.isArray(response.data.searchResults)) {
                    const results = response.data.searchResults;
                    console.log('Search results:', results);
                    setSearchResults(results);
                    const reportSources = response.data.reportSources;
                    setReportSources(reportSources);
                } else {
                    console.error('Search results is not an array or undefined:', response.data);
                    setSearchResults([]);
                    setReportSources([]);
                }
                setIsSearching(false);
            })
            .catch(error => {
                console.error('Error performing search:', error);
                if (error.response?.data?.error?.includes('regex')) {
                    setPatternError('Invalid regex pattern. Please check your syntax.');
                }
                setIsSearching(false);
            });
    };

    // Clear search and show all report sources
    const handleClearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setReportSources(reportSources);
    };

    // Handle clicking on a search result to view document
    const handleViewDocument = (documentId: string) => {
        if (!authLoaded) return;
        
        setLoadingDocument(true);
        setSelectedDocumentId(documentId);
        setDocumentText(null);
        setDocumentUrl(null);
        
        // Find document source details
        const source = reportSources.find(source => source._id === documentId);
        setDocumentTitle(source?.name || 'Document');
        
        // Find all chunks related to this document
        const relatedChunks = searchResults.filter(
            result => result.entity.document_id === documentId
        );
        setDocumentChunks(relatedChunks);
        
        const documentHeaders: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        
        if (token) {
            documentHeaders['Authorization'] = `Bearer ${token}`;
        }

        axios.get<DocumentDetails>(`${apiUrl}/documents/${documentId}`, {
            headers: documentHeaders
        })
            .then(response => {
                setDocumentText(response.data.extracted_text);
                setDocumentDetails(response.data);
                
                // Get URL and PDF status for iframe display
                if (response.data.url) {
                    setDocumentUrl(response.data.url);
                    setIsPdf(response.data.is_pdf || false);
                } else {
                    setDocumentUrl(null);
                }
                setLoadingDocument(false);
            })
            .catch(error => {
                console.error('Error fetching document:', error);
                setDocumentText('Error loading document. Please try again.');
                setLoadingDocument(false);
            });
    };
    
    // Handle clearing the selected document
    const clearSelectedDocument = () => {
        setSelectedDocumentId(null);
        setDocumentText(null);
        setDocumentUrl(null);
        setDocumentTitle('');
    };

    // Group search results by document
    const groupResultsByDocument = () => {
        const groupedResults: Record<string, { source?: ReportSource, chunks: SearchResult[] }> = {};
        searchResults.forEach(result => {
            // Get document ID from either source._id or entity.document_id
            const documentId = result.source?._id || result.entity.document_id;
            if (!documentId) return;
            
            if (!groupedResults[documentId]) {
                // Find source info - either from the result.source or from reportSources
                const source = result.source || 
                    reportSources.find(source => source._id === documentId);
                
                groupedResults[documentId] = {
                    source: source as ReportSource,
                    chunks: []
                };
            }
            groupedResults[documentId].chunks.push(result);

        });
        return groupedResults;
    };
    
    // Create a list of all documents from search results
    const getAllDocumentsFromResults = () => {
        const documents = [] as Array<{_id: string, name?: string, filename?: string}>;
        reportSources.forEach(source => {
            if (!documents.some(doc => doc._id === source._id)) {
                documents.push({
                    _id: source._id,
                    filename: source.metadata?.title || source.filename
                });
            }
        });
        return documents;
    };
    
    // Handle selecting a document from the sidebar
    const handleSelectDocumentFromSidebar = (docId: string) => {
        handleViewDocument(docId);
    };

    // Handle clicking on a document row to toggle showing/hiding chunks
    const handleToggleDocumentChunks = (documentId: string) => {
        setExpandedDocumentId(expandedDocumentId === documentId ? null : documentId);
    };

    // Get chunks for a specific document
    const getChunksForDocument = (documentId: string) => {
        return searchResults.filter(result => 
            result.entity.document_id === documentId || result.source?._id === documentId
        );
    };

    // Helper function to format the Milvus score
    const formatMilvusScore = (distance?: number) => {
        if (distance === undefined) return 'N/A';
        
        // Convert distance to score (higher is better)
        const score = 1 - distance;
        return score.toFixed(4);
    };

    // Function to highlight the search query terms in the text
    const highlightMatches = (text: string) => {
        if (!searchQuery || !text) return text;
        
        // First, try to find exact matches of the whole query
        interface ExactMatch {
            index: number;
            length: number;
            term: string;
        }
        
        const exactMatches: ExactMatch[] = [];
        if (searchQuery.length > 3) {
            const exactRegex = new RegExp(escapeRegExp(searchQuery), 'gi');
            let exactMatch;
            while ((exactMatch = exactRegex.exec(text)) !== null) {
                exactMatches.push({
                    index: exactMatch.index,
                    length: searchQuery.length,
                    term: searchQuery
                });
            }
        }
        
        // Split the search query into individual terms, preserve numbers with commas/decimals
        const searchTerms = searchQuery
            .toLowerCase()
            .split(/\s+/)
            .filter(term => {
                // Include terms with at least 3 characters or numeric values
                return term.length >= 3 || /[\d.,]+/.test(term);
            });
        
        if (searchTerms.length === 0 && exactMatches.length === 0) return text;
        
        // First split the text by newlines to preserve formatting
        const lines = text.split('\n');
        
        return lines.map((line, lineIndex) => {
            // Check for exact matches in this line
            const lineExactMatches = exactMatches.filter(match => {
                const matchPos = match.index;
                // Calculate offset in the original text
                let offset = 0;
                for (let i = 0; i < lineIndex; i++) {
                    offset += lines[i].length + 1; // +1 for the newline character
                }
                // Check if the match is within this line
                return matchPos >= offset && matchPos < offset + line.length;
            });
            
            // Create a regex pattern that handles numbers with commas and decimals
            const numericPattern = searchTerms
                .map(term => {
                    // Special handling for numeric terms
                    if (/^[\d.,]+$/.test(term)) {
                        // Escape the term for regex to handle commas and dots as literals
                        return escapeRegExp(term);
                    }
                    return term;
                })
                .join('|');
            
            // If no search terms after filtering, just return the line
            if (numericPattern === '') {
                if (lineExactMatches.length > 0) {
                    // Handle exact matches
                    let lastIndex = 0;
                    const parts = [];
                    
                    // Sort exact matches by index
                    lineExactMatches.sort((a, b) => {
                        // Calculate adjusted indices for this line
                        let offsetA = a.index;
                        let offsetB = b.index;
                        for (let i = 0; i < lineIndex; i++) {
                            offsetA -= lines[i].length + 1;
                            offsetB -= lines[i].length + 1;
                        }
                        return offsetA - offsetB;
                    });
                    
                    // Process exact matches
                    for (const match of lineExactMatches) {
                        // Calculate adjusted index for this line
                        let adjustedIndex = match.index;
                        for (let i = 0; i < lineIndex; i++) {
                            adjustedIndex -= lines[i].length + 1;
                        }
                        
                        // Add text before the match
                        if (adjustedIndex > lastIndex) {
                            parts.push(<span key={`${lineIndex}-before-${adjustedIndex}`}>{line.substring(lastIndex, adjustedIndex)}</span>);
                        }
                        
                        // Add highlighted match
                        parts.push(
                            <mark key={`${lineIndex}-match-${adjustedIndex}`} className="highlighted-text">
                                {line.substring(adjustedIndex, adjustedIndex + match.term.length)}
                            </mark>
                        );
                        
                        lastIndex = adjustedIndex + match.term.length;
                    }
                    
                    // Add text after the last match
                    if (lastIndex < line.length) {
                        parts.push(<span key={`${lineIndex}-after-${lastIndex}`}>{line.substring(lastIndex)}</span>);
                    }
                    
                    return (
                        <React.Fragment key={`line-${lineIndex}`}>
                            {parts}
                            {lineIndex < lines.length - 1 && <br />}
                        </React.Fragment>
                    );
                }
                
                return (
                    <React.Fragment key={`line-${lineIndex}`}>
                        {line}
                        {lineIndex < lines.length - 1 && <br />}
                    </React.Fragment>
                );
            }
            
            const pattern = new RegExp(`(${numericPattern})`, 'gi');
            
            // Split the line based on the pattern
            const parts = line.split(pattern);
            
            // Map parts to React elements with highlights
            const highlightedParts = parts.map((part, i) => {
                // Check if this part matches any search term
                const isMatch = searchTerms.some(term => 
                    part.toLowerCase() === term.toLowerCase()
                );
                
                return isMatch ? 
                    <mark key={`${lineIndex}-${i}`} className="highlighted-text">{part}</mark> : 
                    <span key={`${lineIndex}-${i}`}>{part}</span>;
            });
            
            // Return the line with highlights, with a line break after (except for the last line)
            return (
                <React.Fragment key={`line-${lineIndex}`}>
                    {highlightedParts}
                    {lineIndex < lines.length - 1 && <br />}
                </React.Fragment>
            );
        });
    };

    // Helper function to escape special characters in regex
    const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    // Show loading while auth is loading
    if (!authLoaded) {
        return <div className="loading-indicator">{t('loading.authentication')}</div>;
    }

    return (
        <div className="container">
            <div className="header">
                <div className="search-container">
                    <form onSubmit={handleSearchSubmit} className="search-form">
                        <div className="search-input-group">
                            <input
                                type="text"
                                placeholder={t('search.searchForDocuments')}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="search-input"
                            />
                            <div className="search-buttons">
                                <button type="submit" className="search-button primary-button" disabled={isSearching}>
                                    {isSearching ? t('loading.searching') : t('buttons.search')}
                                </button>
                                {searchQuery && (
                                    <button 
                                        type="button" 
                                        className="secondary-button"
                                        onClick={handleClearSearch}
                                    >
                                        {t('buttons.clear')}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="search-options">
                            <label className="pattern-toggle">
                                <input
                                    type="checkbox"
                                    checked={patternEnabled}
                                    onChange={togglePatternFilter}
                                />
                                <span className="toggle-label">{t('search.findExactMatches')}</span>
                            </label>
                            {patternError && <div className="pattern-error">{patternError}</div>}
                        </div>
                    </form>
                </div>
            </div>

            
            {isSearching ? (
                <div className="loading-indicator">Loading report sources...</div>
            ) : searchResults.length > 0 && searchQuery ? (
                <div>
                    {/* Document viewer */}
                    {selectedDocumentId && (
                        <DocumentDetailView 
                            documentId={selectedDocumentId}
                            documentTitle={documentTitle}
                            documentDetails={documentDetails || undefined}
                            documentText={documentText || ''}
                            documentUrl={documentUrl || ''}
                            isPdf={isPdf}
                            documentChunks={documentChunks}
                            loadingDocument={loadingDocument}
                            clearSelectedDocument={clearSelectedDocument}
                            allDocuments={getAllDocumentsFromResults()}
                            onSelectDocument={handleSelectDocumentFromSidebar}
                            searchQuery={searchQuery}
                        />
                    )}
                    
                    {/* Search results and report sources only shown when no document is selected */}
                    {!selectedDocumentId && (
                        <>
                            {searchResults.length > 0 && searchQuery && (
                                <div className="search-results-container">
                                    <h2>Search Results 
                                        {patternEnabled && searchQuery && (
                                            <span className="pattern-badge">
                                                Exact matches only
                                            </span>
                                        )}
                                    </h2>
                                    
                                    <div className="table-container">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '25%' }}>Title / Filename</th>
                                                    <th style={{ width: '44%' }}>Source URL</th>
                                                    <th style={{ width: '7%' }}>Relevant Chunks</th>
                                                    <th style={{ width: '7%' }}>Size</th>
                                                    <th style={{ width: '7%' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(groupResultsByDocument()).map(([documentId, data]) => {
                                                    const isExpanded = expandedDocumentId === documentId;
                                                    const documentChunks = getChunksForDocument(documentId);
                                                    
                                                    return (
                                                        <React.Fragment key={documentId}>
                                                            <tr 
                                                                onClick={() => handleToggleDocumentChunks(documentId)}
                                                                className={isExpanded ? 'expanded-row' : ''}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <td>
                                                                    {data.source?.metadata?.title || 
                                                                        (data.source?.filename ? 
                                                                            (data.source.filename.length > 50 ?
                                                                                data.source.filename.substring(0, 50) + '...'
                                                                                : data.source.filename)
                                                                            : 'No title/filename')}
                                                                    {data.source?.metadata?.title && data.source?.metadata_validation_details?.title?.confidence && (
                                                                        <span 
                                                                            className="confidence-badge" 
                                                                            title={`Confidence: ${(data.source.metadata_validation_details.title.confidence * 100).toFixed(1)}%`}
                                                                            style={{
                                                                                marginLeft: '8px',
                                                                                fontSize: '0.7em',
                                                                                backgroundColor: data.source.metadata_validation_details.title.confidence >= 0.9 ? '#28a745' : data.source.metadata_validation_details.title.confidence >= 0.7 ? '#ffc107' : '#dc3545',
                                                                                color: 'white',
                                                                                padding: '1px 4px',
                                                                                borderRadius: '8px',
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            {(data.source.metadata_validation_details.title.confidence * 100).toFixed(0)}%
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {data.source?.url ? (
                                                                        <a 
                                                                            href={data.source.url} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="url-cell"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            title={data.source.url}
                                                                        >
                                                                            {data.source.url.length > 140 ?
                                                                                data.source.url.substring(0, 140) + '...'
                                                                                : data.source.url
                                                                            }
                                                                        </a>
                                                                    ) : '-'}
                                                                </td>
                                                                <td>
                                                                    <span 
                                                                        className="chunk-count-badge"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleToggleDocumentChunks(documentId);
                                                                        }}
                                                                        title="Click to view chunks"
                                                                    >
                                                                        {data.chunks.length}
                                                                    </span>
                                                                </td>
                                                                <td>{data.source?.token_size ? `${data.source.token_size.toLocaleString()} tokens` : '-'}</td>
                                                                <td>
                                                                    {data.source?.url && (
                                                                        <a 
                                                                            href={data.source.url} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="source-link"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <i className="fas fa-external-link-alt"></i> Source
                                                                        </a>
                                                                    )}
                                                                    <button 
                                                                        className="view-document-button secondary-button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleViewDocument(documentId);
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-eye"></i> View
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                            
                                                            {/* Display chunks when expanded */}
                                                            {isExpanded && documentChunks.map((chunk, index) => (
                                                                <tr key={`${documentId}-chunk-${index}`} className="chunk-row">
                                                                    <td colSpan={5} className="chunk-cell">
                                                                        <div className="chunk-content">
                                                                            <p>
                                                                                {highlightMatches((chunk.entity.text || "No text available"))}
                                                                            </p>
                                                                            <div className="chunk-metadata">
                                                                                {chunk.entity.page && (
                                                                                    <span className="chunk-page">Page {chunk.entity.page}</span>
                                                                                )}
                                                                                {chunk.distance !== undefined && (
                                                                                    <span className="chunk-relevance">
                                                                                        Score: {formatMilvusScore(chunk.distance)}
                                                                                    </span>
                                                                                )}
                                                                                {chunk.score !== undefined && (
                                                                                    <span className="chunk-relevance">
                                                                                        Score: {chunk.score.toFixed(4)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : (
                searchQuery ? (
                    <div className="no-results">No report sources found matching your search criteria.</div>
                ) : (
                    <></>
                )
            )}
            {isSearching ? (
              <></>
            ) : searchResults.length > 0 && searchQuery ? (
              <div className="search-results-summary">
                  Found {searchResults.length} relevant chunks across {Object.keys(groupResultsByDocument()).length} documents
                  {patternEnabled && searchQuery ? ' (exact matches only)' : ''}
              </div>
            ) : null}

        </div>
    );
};

export default EmbeddingsSearchView;
